import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import { getAuthAmendableAttribute, postAmendAppointment } from 'features/order-tracker/api/orderTrackerApi';
import {
  ERROR,
  ERROR_CHAT,
  WARNING,
} from 'features/order-tracker/pages/AmendAppointmentPage/components/Notification/constants';
import { ModalTypes } from 'features/order-tracker/shared-components/Modal/constants';
import { AppointmentAnalyticService } from 'features/order-tracker/store/sagas/services/appointmentAnalyticsService';
import {
  formatCurrentAppointmentDate,
  formatDate,
  processAmendAppointmentData,
  reverseDateString,
} from 'features/order-tracker/utils';

import {
  CONFIRM_AMENDS_TO_APPOINTMENT_REQUEST,
  FETCH_ORDER_PREAPPOINTMENTS_REQUEST,
  INIT_AMEND_APPOINTMENT_FLOW,
  closeModal,
  confirmAmendsToAppointmentSucceed,
  deleteMilestone,
  fetchOrderPreAppointmentsStart,
  fetchOrderPreAppointmentsSucceed,
  initAmendAppointmentFlowSucceed,
  openModal,
  setCurrentAppointmentInfo,
  setNotificationType,
} from '../actions';
import { getState } from '../selectors';
import { getOrderPreAppointmentsRequest } from './requests/getOrderPreAppointments';
import { getSiteContactsRequest } from './requests/getSiteContacts';

interface PreAppointmentsPayload {
  date: string;
  isInitRequest: boolean;
}

interface InitFlowPayload {
  currentAppointmentDate: string;
  originAppointmentDate: string;
  itemId: string;
  orderKey: string;
}

interface ConfirmPayload {
  infoToConfirm: unknown[];
  confirmRef: React.RefObject<HTMLDivElement | null>;
}

interface Action<T = unknown> {
  type: string;
  payload: T;
}

export function* getOrderPreAppointments(
  { payload: { date, isInitRequest } }: Action<PreAppointmentsPayload>,
): Generator {
  const formattedDate = reverseDateString(date, '-');

  yield put(fetchOrderPreAppointmentsStart());
  const appointmentsData = (yield call(
    getOrderPreAppointmentsRequest, formattedDate, isInitRequest,
  )) as Record<string, unknown>;

  if (isInitRequest) {
    (appointmentsData as Record<string, unknown>).firstAvailableAppointmentDate =
      ((appointmentsData as Record<string, unknown>).preAppointments as Array<{ dateInfo: string }>)[0].dateInfo;
  }

  yield put(fetchOrderPreAppointmentsSucceed(appointmentsData));
}

export function* initAmendAppointmentFlowSaga({
  payload: { currentAppointmentDate, originAppointmentDate, itemId, orderKey },
}: Action<InitFlowPayload>): Generator {
  try {
    yield put(openModal({ modalType: ModalTypes.Loading }));
    yield put(
      setCurrentAppointmentInfo({
        itemId,
        orderKey,
        formattedDate: currentAppointmentDate,
        originDate: originAppointmentDate,
      }),
    );
    yield call(getAuthAmendableAttribute, orderKey, itemId);
    const initDate = formatCurrentAppointmentDate(currentAppointmentDate);
    const [contacts] = (yield all([
      call(getSiteContactsRequest),
      call(
        getOrderPreAppointments,
        {
          payload: { date: formatDate('YYYY-MM-DD', initDate), isInitRequest: true },
        } as Action<PreAppointmentsPayload>,
      ),
    ])) as [unknown];
    yield put(initAmendAppointmentFlowSucceed({ contacts }));
    yield put(closeModal());
  } catch (error) {
    const { message } = error as { message: string };
    const notificationType = message === WARNING ? WARNING : ERROR_CHAT;
    yield put(closeModal());
    yield put(setNotificationType(notificationType));
  }
}

export function* confirmAmendsToAppointment({ payload }: Action<ConfirmPayload>): Generator {
  const {
    amendAppointment,
    orderSummary: { postcode },
  } = (yield select(getState)) as { amendAppointment: Record<string, unknown>; orderSummary: { postcode: string } };
  const {
    initialContactKey,
    selectedContact,
    selectedSlot,
    appointmentInfo: { itemId },
  } = amendAppointment as {
    initialContactKey: string;
    selectedContact: string;
    selectedSlot: string;
    appointmentInfo: { itemId: string };
  };
  const slotInfo = payload.infoToConfirm[1] as { value: string } | undefined;
  const amendAppointmentData = processAmendAppointmentData(
    amendAppointment as unknown as Parameters<typeof processAmendAppointmentData>[0],
    postcode,
    slotInfo,
  );
  const isDateChanged = !!selectedSlot;
  const isSiteContactChanged = selectedContact !== initialContactKey;

  yield put(openModal({ modalType: ModalTypes.LoadingWhileAmending }));
  try {
    yield postAmendAppointment(
      amendAppointmentData as unknown as api.AmendAppointmentData,
      isDateChanged,
      isSiteContactChanged,
    );
    AppointmentAnalyticService.trackAmendApptApiSuccess(payload.confirmRef);
    yield put(confirmAmendsToAppointmentSucceed({ itemId, amendsInfo: payload.infoToConfirm }));
    yield put(deleteMilestone(itemId));
    yield put(closeModal());
  } catch (error) {
    yield put(closeModal());
    yield put(setNotificationType(ERROR));
  }
}

export function* watchAmendAppointment(): Generator {
  yield takeLatest(FETCH_ORDER_PREAPPOINTMENTS_REQUEST, getOrderPreAppointments);
  yield takeLatest(INIT_AMEND_APPOINTMENT_FLOW, initAmendAppointmentFlowSaga);
  yield takeLatest(CONFIRM_AMENDS_TO_APPOINTMENT_REQUEST, confirmAmendsToAppointment);
}
